// War screen (docs/ARCHITECTURE.md §8, GDD §14): the globe stage with the war network, territory glow and city
// status from the Match's setup and last-known city state, hologram glyphs and lock-on lines fed by the mirror's view
// systems, the VFX layer, and the DOM HUD over the canvas (which owns the war's keys and pointer input). The camera
// frames the war theatre on entry (per game mode, THEATRE_CITIES). City art is drawn in screen pixels at every frame's
// zoom, capped per city by local spacing (render/network/cityArt.ts), with sizes and brightness from the presentation
// tuning (tuning/link.ts: the committed values for players; the tuner retunes the running war through TunableWar). So
// are the colours: the screen resolves the palette (render/palette.ts) from the tuning and the player's palette setting,
// and hands it to the globe, every layer, the HUD and the effects.
// Every buffer is sized for the war's capacities (match/warSetup.ts). The screen does not own the Match: the app flow
// disposes it after leaving.

import { Vector3 } from 'three'
import type { GameModeKey } from '@taxwar/content'
import { InvariantViolation, isPlayerId, type CityIndex, type MatchSetupView, type MapCityView } from '@taxwar/net'
import type { PaletteMode, TuningValues } from '@taxwar/tuning'
import type { CreateVfxLayer, HudHandle, LatLonOut, MountHud, VfxLayer } from '../../match/contracts'
import type { Match } from '../../match'
import { capitalOf, warCapacity } from '../../match/warSetup'
import { MIN_CAMERA_DISTANCE } from '../../render/camera/GlobeCameraRig'
import { DEG_TO_RAD, GLOBE_RADIUS, directionToLatLon, latLonToVector3 } from '../../render/geo/globeSpace'
import type { GlobeGeo } from '../../render/geo/polylines'
import { globeLookOf } from '../../render/globe/globeLook'
import { CityStatusLayer } from '../../render/network/CityStatusLayer'
import { cityArtCapsOf, cityArtOf } from '../../render/network/cityArt'
import { TerritoryGlowLayer } from '../../render/network/TerritoryGlowLayer'
import { WarNetworkLayer } from '../../render/network/WarNetworkLayer'
import { paletteOf, type Palette } from '../../render/palette'
import { GlyphLayer } from '../../render/units/GlyphLayer'
import { LockLineLayer } from '../../render/units/LockLineLayer'
import { BuildingViewSystem, LockLineSystem, StrikeViewSystem, UnitViewSystem } from '../../render/units/viewSystems'
import type { Viewport } from '../../render/viewport'
import type { VisualSettings } from '../../render/visualSettings'
import type { CityPreview, TunableWar, ZoomPreset, ZoomReadout } from '../../tuning/link'
import type { Screen, ScreenContext, ScreenFactory } from '../screen'
import { GlobeStage } from './GlobeStage'
import { WarGlobePicker } from './WarGlobePicker'

/** Fraction of the camera's half field of view the framed theatre may fill. */
const THEATRE_FILL = 0.8
/** Below this the cities' mean direction is undefined (a theatre spread around the globe): frame your capital. */
const MIN_THEATRE_CENTRE_LENGTH = 1e-3
const HUD_ROOT_CLASS = 'war-hud'
/** Tuner zoom presets other than the war's opening framing: the camera's distance from the globe centre. */
const ZOOM_PRESET_DISTANCE: Readonly<Record<Exclude<ZoomPreset, 'opening'>, number>> = {
  close: GLOBE_RADIUS * 1.6,
  closest: MIN_CAMERA_DISTANCE,
}

/**
 * GDD §14, per game mode: the cities the camera frames when the war opens. A duel frames every war city; a World War
 * frames the belligerents' own cities (war-start owner is a seat), so it opens on the countries at war, not the globe.
 */
const THEATRE_CITIES: Readonly<Record<GameModeKey, (setup: MatchSetupView) => readonly MapCityView[]>> = {
  duel: (setup) => setup.cities,
  world_war: (setup) => setup.cities.filter((city) => isPlayerId(city.initialOwner)),
}

export function warScreen(geo: GlobeGeo, match: Match, mountHud: MountHud, createVfxLayer: CreateVfxLayer): ScreenFactory<WarScreen> {
  return (context) => new WarScreen(context, geo, match, mountHud, createVfxLayer)
}

export class WarScreen implements Screen, TunableWar {
  private readonly stage: GlobeStage
  private readonly network: WarNetworkLayer
  private readonly territory: TerritoryGlowLayer
  private readonly status: CityStatusLayer
  private readonly glyphs: GlyphLayer
  private readonly lockLines: LockLineLayer
  private readonly hudRoot: HTMLElement
  private readonly hud: HudHandle
  private vfx: VfxLayer
  private settings: VisualSettings
  /** The tuning in force (the committed values, or the tuner's). */
  private values: TuningValues
  /** The tuner's palette preview; null = the player's palette setting (players never preview). */
  private palettePreview: PaletteMode | null = null
  private palette: Palette
  /** Glyphs are sized in CSS pixels; refreshed on every viewport change. */
  private cssPixelsPerUnitAtUnitDepth: number

  constructor(
    context: ScreenContext,
    geo: GlobeGeo,
    private readonly match: Match,
    mountHud: MountHud,
    private readonly createVfxLayer: CreateVfxLayer,
  ) {
    const setup = match.setup
    const settings = context.settings
    const tuning = context.tuning
    this.settings = settings
    this.values = tuning.values
    const palette = paletteOf(tuning.values, settings.palette)
    this.palette = palette
    this.stage = new GlobeStage(context, geo, globeLookOf(tuning.values, palette))
    this.cssPixelsPerUnitAtUnitDepth = this.stage.rig.cssPixelsPerUnitAtUnitDepth(context.viewport)

    const art = cityArtOf(tuning.values)
    const cityArtCaps = cityArtCapsOf(setup, art)
    this.territory = new TerritoryGlowLayer(palette, art)
    this.network = new WarNetworkLayer(palette, art)
    this.status = new CityStatusLayer(palette, art)
    const capacity = warCapacity(setup)
    this.territory.setMap(setup, cityArtCaps)
    this.network.setMap(setup, cityArtCaps)
    this.status.setMap(setup, cityArtCaps)
    this.glyphs = new GlyphLayer(match.you, capacity, art.buildingRingPx, cityArtCaps, palette, settings.reduceEffects)
    this.lockLines = new LockLineLayer(match.you, capacity, palette)
    this.vfx = this.newVfxLayer(settings)
    this.stage.scene.add(this.territory.root, this.network.root, this.status.root, this.glyphs.root, this.lockLines.root)

    for (const city of setup.cities) this.applyCity(city.index)
    const unsubscribe = match.onCityChanged(this.applyCity)
    context.signal.addEventListener('abort', unsubscribe, { once: true })
    match.addPresentationSystem(new UnitViewSystem(this.glyphs), context.signal)
    match.addPresentationSystem(new BuildingViewSystem(this.glyphs, setup), context.signal)
    match.addPresentationSystem(new StrikeViewSystem(this.glyphs), context.signal)
    match.addPresentationSystem(new LockLineSystem(match, this.lockLines), context.signal)

    const canvas = context.renderer.domElement
    this.hudRoot = createHudRoot(canvas)
    this.hud = mountHud(this.hudRoot, match, new WarGlobePicker(this.stage.rig, canvas, setup), settings, palette, context.signal)

    this.frameTheatre(setup)
    context.addUpdater(this.frame)
    tuning.attach(this, context.signal)
  }

  /** TunableWar: every layer's city art, the globe's look and the palette from new values (the tuner's controls). */
  applyTuning(values: TuningValues): void {
    this.values = values
    const art = cityArtOf(values)
    const cityArtCaps = cityArtCapsOf(this.match.setup, art)
    this.network.setCityArt(art, cityArtCaps)
    this.territory.setCityArt(art, cityArtCaps)
    this.status.setCityArt(art, cityArtCaps)
    this.glyphs.setBuildingRing(art.buildingRingPx, cityArtCaps)
    this.applyPalette()
  }

  /** TunableWar: show the normal or the colour-blind palette, whatever the player's setting. */
  previewPalette(mode: PaletteMode): void {
    this.palettePreview = mode
    this.applyPalette()
  }

  /** TunableWar: the status arcs' preview (every city damaged or being captured, or each city's own state). */
  previewCities(preview: CityPreview): void {
    this.status.setPreview(preview)
  }

  /** TunableWar: the war's opening framing, or a preset distance straight above the point under the camera. */
  zoom(preset: ZoomPreset): void {
    if (preset === 'opening') {
      this.frameTheatre(this.match.setup)
      return
    }
    const over: LatLonOut = { lat: 0, lon: 0 }
    directionToLatLon(this.stage.rig.camera.position, over)
    this.stage.rig.frame(over.lat, over.lon, ZOOM_PRESET_DISTANCE[preset])
  }

  /** TunableWar: the zoom the city art is drawn at, and the camera's altitude. */
  readout(): ZoomReadout {
    return { worldPerCssPixel: this.worldPerCssPixel(), altitude: this.stage.rig.camera.position.length() - GLOBE_RADIUS }
  }

  resize(viewport: Viewport): void {
    this.stage.resize(viewport)
    this.cssPixelsPerUnitAtUnitDepth = this.stage.rig.cssPixelsPerUnitAtUnitDepth(viewport)
  }

  applySettings(settings: VisualSettings): void {
    const previous = this.settings
    this.settings = settings
    this.stage.applySettings(settings)
    this.glyphs.setReduceEffects(settings.reduceEffects)
    this.hud.applySettings(settings)
    // The VFX layer takes its quality tier at creation.
    if (settings.quality !== previous.quality) {
      this.stage.scene.remove(this.vfx.root)
      this.vfx.dispose()
      this.vfx = this.newVfxLayer(settings)
    } else {
      this.vfx.setReduceEffects(settings.reduceEffects)
    }
    if (settings.palette !== previous.palette) this.applyPalette()
  }

  dispose(): void {
    this.hud.dispose()
    this.hudRoot.remove()
    this.vfx.dispose()
    this.lockLines.dispose()
    this.glyphs.dispose()
    this.status.dispose()
    this.network.dispose()
    this.territory.dispose()
    this.stage.dispose()
  }

  private newVfxLayer(settings: VisualSettings): VfxLayer {
    const vfx = this.createVfxLayer(this.match, settings.quality, this.palette)
    vfx.setReduceEffects(settings.reduceEffects)
    this.stage.scene.add(vfx.root)
    return vfx
  }

  /** Resolves the palette (the tuning's colours in the previewed or the player's palette) and gives it to everything. */
  private applyPalette(): void {
    const palette = paletteOf(this.values, this.palettePreview ?? this.settings.palette)
    this.palette = palette
    this.stage.setLook(globeLookOf(this.values, palette))
    this.network.setPalette(palette)
    this.territory.setPalette(palette)
    this.status.setPalette(palette)
    this.glyphs.setPalette(palette)
    this.lockLines.setPalette(palette)
    this.vfx.setPalette(palette)
    this.hud.applyPalette(palette)
  }

  private readonly applyCity = (index: CityIndex): void => {
    const state = this.match.cityState(index)
    this.network.setCityOwner(index, state.owner)
    this.territory.setCityOwner(index, state.owner)
    this.status.setCityState(index, state)
  }

  /**
   * GDD §14: looks down on the centre of the mode's theatre cities, from just far enough to fit them all (the rig
   * clamps to its zoom range). Runs once per war.
   */
  private frameTheatre(setup: MatchSetupView): void {
    const theatre = THEATRE_CITIES[setup.mode](setup)
    if (theatre.length === 0) throw new InvariantViolation(`mode ${setup.mode} frames no cities on this war map`)
    const centre = new Vector3()
    const city = new Vector3()
    for (const view of theatre) centre.add(latLonToVector3(view.lat, view.lon, 1, city))
    if (centre.length() / theatre.length < MIN_THEATRE_CENTRE_LENGTH) {
      const capital = capitalOf(setup, setup.you)
      latLonToVector3(capital.lat, capital.lon, 1, centre)
    }
    centre.normalize()
    let spread = 0
    for (const view of theatre) spread = Math.max(spread, centre.angleTo(latLonToVector3(view.lat, view.lon, 1, city)))
    const camera = this.stage.rig.camera
    const halfVertical = (camera.fov * DEG_TO_RAD) / 2
    const halfHorizontal = Math.atan(Math.tan(halfVertical) * camera.aspect)
    const halfView = Math.min(halfVertical, halfHorizontal) * THEATRE_FILL
    // A point at angle `spread` from the view axis on the unit globe is seen at atan(sin s / (d − cos s)) off-axis.
    const distance = Math.cos(spread) + Math.sin(spread) / Math.tan(halfView)
    const over: LatLonOut = { lat: 0, lon: 0 }
    directionToLatLon(centre, over)
    this.stage.rig.frame(over.lat, over.lon, distance)
  }

  /** Render units per CSS pixel at the globe surface under the camera. */
  private worldPerCssPixel(): number {
    const altitude = this.stage.rig.camera.position.length() - GLOBE_RADIUS
    return altitude / this.cssPixelsPerUnitAtUnitDepth
  }

  private readonly frame = (dtSeconds: number): void => {
    this.stage.update(dtSeconds)
    const worldPerCssPixel = this.worldPerCssPixel()
    this.glyphs.beginFrame(worldPerCssPixel)
    this.lockLines.begin()
    this.match.frame(dtSeconds)
    this.glyphs.endFrame()
    this.lockLines.end()
    this.glyphs.update(dtSeconds)
    this.network.update(dtSeconds, worldPerCssPixel)
    this.territory.update(worldPerCssPixel)
    this.status.update(worldPerCssPixel)
    this.vfx.update(dtSeconds, worldPerCssPixel)
    this.hud.update(dtSeconds)
    this.stage.render(dtSeconds)
  }
}

/** The HUD overlay covers the canvas; it passes pointer events through except where HUD elements opt back in. */
function createHudRoot(canvas: HTMLCanvasElement): HTMLElement {
  const host = canvas.parentElement
  if (host === null) throw new InvariantViolation('the renderer canvas is not attached to a host element')
  const root = document.createElement('div')
  root.className = HUD_ROOT_CLASS
  root.style.position = 'absolute'
  root.style.inset = '0'
  root.style.pointerEvents = 'none'
  host.appendChild(root)
  return root
}
